import { useState } from 'react';
import { RunButton, InfoCard, NoResult, AiAdvisor } from './common';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';

const Weather = ({ farm, selectedCrop, crops, year, season }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const label = crops.find(c => c.key === selectedCrop)?.label;

  const run = async () => {
    setLoading(true); setErrMsg('');
    try {
      const r = await api.weather({ crop_key: selectedCrop, lat: farm.lat, lon: farm.lon, year, season });
      setResult(r);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Weather advisory failed.';
      setErrMsg(msg);
      toast.error('Weather advisory failed');
    } finally { setLoading(false); }
  };

  return (
    <InfoCard title={`Weather Forecast & Alerts · ${label}`}
              subtitle="7-day past + forecast (Open-Meteo, free) with GDD tracking and heat/cold/rain/disease alerts.">
      <RunButton testId={APP.actions.runWeather} onClick={run} loading={loading}>Get forecast &amp; alerts</RunButton>
      {errMsg && (
        <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
          <b>Weather data unavailable:</b> {errMsg}. This advisory uses the Open-Meteo public API; try again in a few minutes or check your connection.
        </div>
      )}
      {!result && !loading && !errMsg && <div className="mt-4"><NoResult /></div>}
      {result && (
        <div className="mt-5 space-y-4 animate-fade-up">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ['Stage', result.stage, ''],
              ['DAS', result.das, ''],
              ['Cum GDD', result.cum_gdd, '°C·d'],
              ['7-day past rain', result.past?.total_rain_mm, 'mm'],
              ['7-day fc rain', result.forecast?.total_rain_mm, 'mm'],
            ].map(([l, v, u]) => (
              <div key={l} className="rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-3">
                <div className="text-[10px] uppercase text-icrisat-earth">{l}</div>
                <div className="font-display text-icrisat-green">{v ?? '—'} <span className="text-xs">{u}</span></div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-icrisat-green/20 p-4 bg-white space-y-2">
            <h4 className="font-medium text-icrisat-green">Advisories</h4>
            <ul className="space-y-2">
              {result.advisories.map((a) => (
                <li key={a.slice(0, 80)} className="text-sm leading-relaxed">{a}</li>
              ))}
            </ul>
          </div>

          {result.forecast_table?.length > 0 && (
            <div>
              <h4 className="font-medium text-icrisat-green mb-2">7-day forecast</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Tmax (°C)</TableHead>
                    <TableHead className="text-right">Tmin (°C)</TableHead>
                    <TableHead className="text-right">Rain (mm)</TableHead>
                    <TableHead className="text-right">ET0 (mm)</TableHead>
                    <TableHead className="text-right">Wind (km/h)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.forecast_table.map((r) => (
                    <TableRow key={r.Date}>
                      <TableCell>{r.Date}</TableCell>
                      <TableCell className="text-right">{r['Tmax (°C)']?.toFixed?.(1) ?? r['Tmax (°C)']}</TableCell>
                      <TableCell className="text-right">{r['Tmin (°C)']?.toFixed?.(1) ?? r['Tmin (°C)']}</TableCell>
                      <TableCell className="text-right">{r['Rain (mm)']?.toFixed?.(1) ?? r['Rain (mm)']}</TableCell>
                      <TableCell className="text-right">{r['ET0 (mm)']?.toFixed?.(1) ?? r['ET0 (mm)']}</TableCell>
                      <TableCell className="text-right">{r['Wind (km/h)']?.toFixed?.(1) ?? r['Wind (km/h)']}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <AiAdvisor context="weather" payload={result} />
        </div>
      )}
    </InfoCard>
  );
};

export default Weather;
